import './exportGridWindowStore.less'
import React from 'react';
import { Observer } from 'mobx-react';
import { serialize } from 'serializr';
import { makeAutoObservable } from 'mobx';

import { CustomDataEntry } from 'controls/designer/customDataEntry';
import { CustomDataListEntry } from 'controls/designer/features/widgets/custom-data-list/customDataListEntry';
import { getDataSourcesList } from 'controls/designer/features/widgets/custom-data-list/api';
import { populateCustomDataExtraFields } from 'controls/designer/features/widgets/custom-data-list/utils/populateCustomDataExtraFields';
import { GridStore } from 'controls/grid/gridStore';
import { ArrayDataProvider } from 'controls/grid/arrayDataProvider';
import { GridSelectionType } from 'controls/grid/gridConfiguration';
import { RuleConfigurationBuilder } from 'controls/queryBuilder/ruleDefinition';
import { Grid } from 'controls/grid/grid';
import { DataSizeIndicator } from 'controls/grid/dataSizeIndicator';
import { GridSearchInput } from 'controls/grid/gridSearchInput';
import { Section } from 'controls/react/layout/section';
import { Toolbar } from 'controls/react/layout/toolbar';
import { AntSelect } from 'controls/react/ant/antSelect';
import { AntButton } from 'controls/react/ant/antButton';
import { openModal } from 'controls/react/ant/antModal';
import { DesignerStore } from 'controls/designer/designerStore';
import { MobxManager } from 'framework/mobx-integration';
import {apiFetch, apiFetchObservable, CachedValue} from 'framework/api';
import {exportDataSourceWidget, getExportFormats} from 'controls/designer/features/widgets/export/api';
import { i18n } from './localization';
import {
	DataSourceExportWidgetConfig,
	DatasourceWidgetTypes,
	ExportFormatOption
} from "controls/designer/features/widgets/export/types";
import {
	GetDatasourceFiltersConfigurationArgs,
	getDataSourcePayload
} from "controls/designer/features/widgets/datasources/api";
import {TimePeriod} from "controls/react/form/timePeriodSelector";
import {getFileByStreamId} from "framework/getFileByStreamId";
import {FormEntryNew} from "controls/react/form/formEntryNew";
import b_ from "b_";

const b = b_.with('export-grid-window');
export class ExportGridWindowStore {
	gridStore: GridStore<CustomDataListEntry>;
	mobx = new MobxManager();
	opened: boolean = false;
	selectedFormat: string = 'XLSX';
	formats: CachedValue<ExportFormatOption[]>;

	get widgets() {
		return this.designerStore.customData
			.filter((x) => x.widgetConfig != null && DatasourceWidgetTypes.includes(x.widgetConfig.type))
			.map((x) => serialize(x) as CustomDataEntry)
	}

	constructor(public designerStore: DesignerStore) {
		makeAutoObservable(this);
		this.formats = apiFetchObservable(getExportFormats());
	}

	async open() {
		if (this.opened) return;

		this.createGridStore();
		this.opened = true;

		const openResult = openModal(() => (
			{
				width: 1200,
				height: 450,
				style: { top: 170 },
				mask: false,
				className: b(),
				footer: (
					<Observer>
						{() => (
							<Toolbar>
								<FormEntryNew label={i18n('Format')} oldStyle={true} model={this} modelField={'selectedFormat'}>
									<AntSelect
										placeholder={i18n('Format')}
										options={this.formats.data?.map((x) => ({ value: x.value, label: x.text }))}
										loading={this.formats.loading}
										width={220}
									/>
								</FormEntryNew>
								<AntButton
									type={'primary'}
									disabled={!this.selectedFormat || !this.gridStore?.selection?.dataset?.items?.length}
									onClick={async () => {
										await this.onExport();
										openResult.close(true);
									}}
								>
									{i18n('Export')}
								</AntButton>
								<AntButton onClick={() => openResult.close()}>
									{i18n('Cancel')}
								</AntButton>
							</Toolbar>
						)}
					</Observer>
				),
			}),
			<Observer>
				{() => {
					if (!this.gridStore) return null;

					return (
						<Section contentPadding={false}>
							<Toolbar title={i18n('Export')}>
								<DataSizeIndicator store={this.gridStore} />
								<GridSearchInput store={this.gridStore} />
							</Toolbar>
							<Grid store={this.gridStore} />
						</Section>
					);
				}}
			</Observer>,
		);

		await openResult.promise;

		this.opened = false;
	}

	createGridStore() {
		if (this.gridStore != null) return;

		this.mobx.reaction(
			() => {
				return getDataSourcesList({
					dataSources: this.widgets,
				})
			},
			async (request) => {
				const response = await apiFetch(request);

				if (!response.success) {
					return;
				}

				populateCustomDataExtraFields(response.data, this.designerStore.customData);

				const filters = RuleConfigurationBuilder.create<CustomDataListEntry>()
					.addText('name', i18n('Widget name'))
					.addText('customDataType', i18n('Widget type'))
					.addText('customDataName', i18n('Name'))
					.build();

				this.gridStore = new GridStore<CustomDataListEntry>({
					dataProvider: new ArrayDataProvider(response.data, filters),
					selection: GridSelectionType.Many,
					columns: [
						{
							field: 'customDataName',
							title: i18n('Datasource'),
							renderer: (item) => ({
								content: item.customDataName,
								onClick: () => this.selectCell(item),
							}),
							minWidth: 200,
						},
						{
							field: 'name',
							title: i18n('Widget name'),
							renderer: (item) => ({
								content: item.name,
								onClick: () => this.selectCell(item),
							}),
							width: 200,
						},
						{
							field: 'customDataType',
							title: i18n('Widget type'),
							renderer: (item) => ({
								content: item.customDataType,
								onClick: () => this.selectCell(item),
							}),
							width: 200,
						},
						{
							field: 'notes',
							title: i18n('Notes'),
							expandOnClick: false,
							renderer: (item: CustomDataListEntry) => {
								const customData = this.getCustomData(item);
								if (!customData) return null;

								const noteKey = item.customDataId ? item.customDataId : 'default';

								return {
									content: <>{customData.notes[noteKey]}</>,
									onClick: () => this.selectCell(item),
								};
							},
							minWidth: 100,
						},
					],
				});
			},
			{
				fireImmediately: true,
			},
		);
	}

	selectCell = (item: CustomDataListEntry) => {
		const customData = this.getCustomData(item);
		if (customData?.cell == null) {
			return;
		} else {
			this.designerStore.graph.setSelectionCells([customData.cell]);
		}
	};

	getCustomData = (item: CustomDataListEntry) => {
		return this.designerStore.customData.find((x) => x.id == item.id);
	};

	onExport = async () => {
		if (!this.selectedFormat || !this.gridStore?.selection?.dataset?.items?.length) {
			return;
		}

		const selected = this.gridStore.selection.dataset.items
			.map((x) => ({ widgetConfig: this.getCustomData(x.data)?.widgetConfig, datasourceName: x.data.customDataName }))
			.filter((x) => x.widgetConfig != null);
		const payloads = selected.map(x => extendWidgetPayloadForExport(getDataSourcePayload(x.widgetConfig as never as GetDatasourceFiltersConfigurationArgs), x.widgetConfig, this.designerStore.timeSettings?.period, x.datasourceName));
		const results = await apiFetch(exportDataSourceWidget({format: this.selectedFormat, payload: payloads}));
		if (results.success) {
			getFileByStreamId(results.data);
		}
	};

	destroy() {
		this.mobx.destroy();
	}
}

const extendWidgetPayloadForExport = (payload: object, widgetConfig: DataSourceExportWidgetConfig, period: TimePeriod, datasourceName: string) => {
	return Object.assign(payload, {
		filter: {},
		sort: [],
		skip: 0,
		take: 999,
		exportFilename: datasourceName,
		dataSourceId:
			widgetConfig.dataSourceSelection?.dataSourceId ?? widgetConfig.dataSourceId ?? widgetConfig.datasourceId,
		accountId: widgetConfig.dataSourceSelection?.accountId ?? widgetConfig.accountId,
		period: widgetConfig.period ?? period,
	});
}
